import { httpValueSchema } from '@ze-great-dashboard/shared/browser'
import { errorPresentation } from './error-presentation.ts'
import styles from './HttpValuePanel.module.css'
import { PanelEvidence, PanelFrame, PanelHint, PanelStatus, PanelValue } from './PanelFrame.tsx'
import type { HttpValueFactObservation, PanelProps } from './panel-props.ts'
import { ObservedAt, UpdateHealth } from './TimeAge.tsx'

export function HttpValuePanel({
  panel,
  envelope,
  attentionActive,
  updateHealth,
  facts,
}: PanelProps) {
  if (panel.facts)
    return <GroupedHttpValuePanel panel={panel} attentionActive={attentionActive} facts={facts} />
  if (!envelope)
    return (
      <PanelFrame panel={panel} attentionActive={attentionActive}>
        <PanelEvidence>
          <PanelHint>Loading…</PanelHint>
        </PanelEvidence>
      </PanelFrame>
    )
  if (envelope.state === 'error') {
    const presentation = errorPresentation(envelope.error.kind)
    return (
      <PanelFrame panel={panel} envelope={envelope} attentionActive={attentionActive} error>
        <PanelEvidence>
          <PanelStatus glyph="⚠" label={presentation.label} emphasis={presentation.emphasis} />
          <PanelHint>{envelope.error.message}</PanelHint>
        </PanelEvidence>
      </PanelFrame>
    )
  }
  const signal = httpValueSchema.safeParse(envelope.signal)
  if (!signal.success)
    return (
      <PanelFrame panel={panel} envelope={envelope} attentionActive={attentionActive} error>
        <PanelEvidence>
          <PanelStatus glyph="⚠" label="Invalid value" />
        </PanelEvidence>
      </PanelFrame>
    )
  return (
    <PanelFrame panel={panel} envelope={envelope} attentionActive={attentionActive}>
      <PanelEvidence className={styles.fact}>
        <PanelStatus glyph="✓" label="Value read" compactOnly />
        <PanelValue>{String(signal.data.value)}</PanelValue>
        <ObservedAt value={envelope.observedAt} />
        {updateHealth && <UpdateHealth health={updateHealth} />}
      </PanelEvidence>
    </PanelFrame>
  )
}

function GroupedHttpValuePanel({
  panel,
  attentionActive,
  facts,
}: Pick<PanelProps, 'panel' | 'attentionActive' | 'facts'>) {
  if (!panel.facts) return null
  return (
    <PanelFrame panel={panel} attentionActive={attentionActive}>
      <PanelEvidence>
        <div className={styles.facts} data-http-value-facts>
          {panel.facts.map((fact) => (
            <HttpValueFactCell key={fact.id} label={fact.label} observation={facts?.[fact.id]} />
          ))}
        </div>
      </PanelEvidence>
    </PanelFrame>
  )
}

function HttpValueFactCell({
  label,
  observation,
}: {
  label: string
  observation: HttpValueFactObservation | undefined
}) {
  const envelope = observation?.envelope
  if (!envelope)
    return (
      <section className={styles.factCell} data-http-value-fact>
        <h3>{label}</h3>
        <PanelValue emphasis={observation?.failure ? 'serious' : undefined}>
          {observation?.failure ? '⚠ Updates unavailable' : 'Loading…'}
        </PanelValue>
        {observation?.updateHealth && <UpdateHealth health={observation.updateHealth} />}
      </section>
    )
  if (envelope.state === 'error') {
    const presentation = errorPresentation(envelope.error.kind)
    return (
      <section className={styles.factCell} data-http-value-fact data-error>
        <FactSourceLink label={label} link={envelope.link} />
        <h3>{label}</h3>
        <PanelStatus glyph="⚠" label={presentation.label} emphasis={presentation.emphasis} />
        <PanelHint>{envelope.error.message}</PanelHint>
        <ObservedAt value={envelope.observedAt} />
      </section>
    )
  }
  const signal = httpValueSchema.safeParse(envelope.signal)
  if (!signal.success)
    return (
      <section className={styles.factCell} data-http-value-fact data-error>
        <FactSourceLink label={label} link={envelope.link} />
        <h3>{label}</h3>
        <PanelStatus glyph="⚠" label="Invalid value" emphasis="serious" />
        <ObservedAt value={envelope.observedAt} />
      </section>
    )
  return (
    <section className={styles.factCell} data-http-value-fact>
      <FactSourceLink label={label} link={envelope.link} />
      <h3>{label}</h3>
      <PanelValue>{String(signal.data.value)}</PanelValue>
      <ObservedAt value={envelope.observedAt} />
    </section>
  )
}

function FactSourceLink({ label, link }: { label: string; link: string | null }) {
  if (!link) return null
  return (
    <a
      className={styles.factSource}
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`View source for ${label} (opens in a new tab)`}
      title={`View source for ${label} (opens in a new tab)`}
    >
      <span aria-hidden="true">↗</span>
      <span className="screen-reader-only">View source</span>
    </a>
  )
}
